<template>
  <div
    class="relative flex w-full cursor-pointer items-center justify-between gap-x-5 overflow-hidden rounded-[12px] bg-white px-4 py-[10px] text-gray-800 transition-colors after:pointer-events-none after:absolute after:inset-0 after:z-[5] after:rounded-[12px] after:border after:content-['']"
    :class="classes"
    @click="handleClick"
  >
    <PlanCallout v-if="isAnnual">
      Annual &bull; Save additional {{ discount }}%
    </PlanCallout>

    <div>
      <div class="text-[14px] font-normal leading-[22px] text-gray-800">
        {{ title }}
      </div>

      <template v-if="isPAYG">
        <div class="flex items-baseline gap-x-[6px] text-gray-800">
          <span
            v-if="isAnnual"
            class="text-[22px] font-medium leading-[28px] text-red-500 line-through"
          >
            {{ originalPAYGPrice }}
          </span>
          <span class="text-[22px] font-medium leading-[28px]">
            {{ paygPrices.subscription?.details?.renderedPrice }}
          </span>
          <span class="text-[15px] font-medium italic leading-[25px]">
            {{ t(paygPrices.subscription?.details?.suffix) }}
          </span>
        </div>

        <div class="flex items-baseline gap-x-[6px] text-gray-800">
          <span class="text-[18px] font-medium leading-[22px]">
            {{ paygPrices.copyright?.details?.renderedPrice }}
          </span>
          <span
            v-if="copyrightSuffix.label"
            class="text-[15px] font-medium italic leading-[20px]"
          >
            {{ copyrightSuffix.label }}
          </span>
          <span
            v-if="copyrightSuffix.fine"
            class="text-[12px] font-normal italic leading-[17px]"
          >
            {{ copyrightSuffix.fine }}
          </span>
        </div>
      </template>

      <div v-else class="flex items-baseline gap-x-[6px] text-gray-800">
        <span
          v-if="isAnnual"
          class="text-[22px] font-medium leading-[28px] text-red-500 line-through"
        >
          {{ originalBundlePrice }}
        </span>
        <span class="text-[22px] font-medium leading-[28px]">
          {{ lowestActivePlanItem?.prices[0].details?.renderedPrice }}
        </span>
        <span class="text-[15px] font-medium italic leading-[25px]">
          {{ t(lowestActivePlanItem?.prices[0].details?.suffix) }}
        </span>
      </div>
    </div>

    <Checkbox v-model="selected" @click.stop />
  </div>
</template>

<script setup lang="ts">
import {
  PlanItemStatus,
  PlanPriceType,
  type Plan,
} from '@/shared/graphql/legacy/generated'
import { splitFine } from '@/features/billing/utils/fine'
import { t } from '@/shared/i18n'
import { useWebviewStore } from '@/shared/stores/webview'
import { formatPrice } from '@/shared/utils/money'
import { Checkbox } from '@dacr/library'
import { computed } from 'vue'
import PlanCallout from './PlanCallout.vue'

const props = defineProps<{
  modelValue?: Plan
  plan: Plan
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', plan: Plan): void
}>()

const webviewStore = useWebviewStore()

const selected = computed({
  get() {
    return props.modelValue?.id === props.plan.id
  },
  set() {
    handleClick()
  },
})

const lowestActivePlanItem = computed(() =>
  props.plan.items.find(
    (item, index, arr) =>
      item.status === PlanItemStatus.Active || index === arr.length - 1
  )
)

const isAnnual = computed(
  () =>
    lowestActivePlanItem.value?.prices.find(
      (price) => price.type === PlanPriceType.Subscription
    )?.details?.billingCycleType === 'year'
)

const discount = computed(() => (props.plan.items.length > 1 ? 25 : 40))

const title = computed(() =>
  isAnnual.value ? 'Billed annually' : 'Billed monthly'
)

const originalPAYGPrice = computed(() => {
  const price = lowestActivePlanItem.value?.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  let amount = (price?.details?.amount || 0) / 100
  if (!webviewStore.isActive) {
    amount = amount / 12
  }
  return formatPrice({
    amount: Math.round(amount / (1 - discount.value / 100)),
    currency: price?.details?.currency,
    hideDecimals: true,
  })
})

const originalBundlePrice = computed(() => {
  const price = lowestActivePlanItem.value?.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  let amount = (price?.details?.amount || 0) / 100
  if (!webviewStore.isActive) {
    amount = amount / 12
  }
  return formatPrice({
    amount: Math.round(amount / (1 - discount.value / 100)),
    currency: price?.details?.currency,
    hideDecimals: true,
  })
})

const classes = computed(() => ({
  'after:border-gray-800': selected.value,
  'after:border-[#ebebeb] hover:bg-[#f9f9fa]': !selected.value,
}))

const isPAYG = computed(() => props.plan.items.length === 1)

const paygPrices = computed(() => {
  const subscription = props.plan.items[0].prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )
  const copyright = props.plan.items[0].prices.find(
    (price) => price.type === PlanPriceType.File
  )
  return { subscription, copyright }
})

const copyrightSuffix = computed(() =>
  splitFine(t(paygPrices.value.copyright?.details?.suffix) ?? '')
)

const handleClick = () => {
  emit('update:modelValue', props.plan)
}
</script>
